import {Inject, Injectable} from '@angular/core';
import {defer, forkJoin, Observable, of, tap} from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
  DocumentTemplate,
  DocumentTemplateAuditEntry,
  DocumentTemplateUsage,
  Eviction,
  FillableFieldSection,
  TemplateFieldReport
} from '@ee/common/models';
import {
  degrees,
  PDFArray,
  PDFDict,
  PDFDocument, PDFFont,
  PDFHexString,
  PDFName,
  PDFString,
  rgb,
  PDFOperatorNames as Ops,
  PDFNumber, PDFContentStream, popGraphicsState, drawText, pushGraphicsState, translate, rotateDegrees, asPDFName, PDFOperator
} from 'pdf-lib';
import {DocumentService} from './document.service';
import {AttachmentType} from '@ee/common/enums';


@Injectable({ providedIn: 'root' })
export class FormService {
  private cachedForms$: Observable<DocumentTemplate[]> | null = null;
  /** id -> template, for templates resolved via resolveForms(). */
  private readonly resolvedFormsById = new Map<string, DocumentTemplate>();
  /** Ids resolveForms() has confirmed resolve to nothing, so they are not re-requested. */
  private readonly unresolvableFormIds = new Set<string>();
  /** In-flight resolveForms() requests keyed by the ids they cover, to dedupe concurrent callers. */
  private readonly inFlightFormIds = new Map<string, Observable<DocumentTemplate[]>>();

  constructor(private http: HttpClient, private documentService: DocumentService,
              @Inject('environment') private environment) {
  }

  findById(id: string): Observable<DocumentTemplate> {
    return this.http.get<DocumentTemplate>(`${this.environment.api_prefix}api/forms/${id}`);
  }

  /**
   * Which document templates merge each field, keyed by storage key.
   *
   * The Case Fields registry uses this to refuse deleting a field a template still merges. Until it
   * existed such a field was deletable: the template kept its token, the token stopped resolving, and
   * the next document rendered a blank where the number used to be.
   */
  getFieldUsage(): Observable<Record<string, { id: string; name: string }[]>> {
    return this.http.get<Record<string, { id: string; name: string }[]>>(
      `${this.environment.api_prefix}api/forms/field-usage`
    );
  }

  cloneForm(formId: string): Observable<DocumentTemplate> {
    return this.http.post<DocumentTemplate>(`${this.environment.api_prefix}api/forms/${formId}/clone`, {}).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  /** @deprecated Use updateFormFromUpload instead. Sends file bytes through the app server. */
  updateForm(formData: FormData): Observable<DocumentTemplate> {
    return this.http.put<DocumentTemplate>(`${this.environment.api_prefix}api/forms`, formData).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  /**
   * Makes an earlier stored file the active one. A repoint on the server — the version's object has been
   * in S3 since it was replaced — returning the refreshed template with fields rescanned from it.
   */
  activateTemplateVersion(formId: string, versionId: string): Observable<DocumentTemplate> {
    return this.http
      .post<DocumentTemplate>(`${this.environment.api_prefix}api/forms/${formId}/versions/${versionId}/activate`, {})
      .pipe(tap(() => this.invalidateFormsCache()));
  }

  /** @deprecated Use saveFormFromUpload instead. Sends file bytes through the app server. */
  saveForm(formData: FormData): Observable<DocumentTemplate> {
    return this.http.post<DocumentTemplate>(`${this.environment.api_prefix}api/forms`, formData).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  /**
   * Creates a new document template using the upload-on-select flow. The template
   * file (if any) was already uploaded to the staging bucket via ee-uploader and is
   * referenced by `upload_id`; no file bytes pass through the app server. When no file
   * was provided, `upload_id` is omitted from the payload entirely.
   */
  saveFormFromUpload(form: DocumentTemplate, uploadId?: string | null): Observable<DocumentTemplate> {
    const body: { form: DocumentTemplate; upload_id?: string } = { form };
    if (uploadId) {
      body.upload_id = uploadId;
    }
    return this.http.post<DocumentTemplate>(`${this.environment.api_prefix}api/forms/from-upload`, body).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  /**
   * Updates an existing document template using the upload-on-select flow. When the
   * user replaced the template file, `upload_id` references the pre-staged, pre-scanned
   * object; when omitted, the existing template file is retained.
   */
  updateFormFromUpload(form: DocumentTemplate, uploadId?: string | null): Observable<DocumentTemplate> {
    const body: { form: DocumentTemplate; upload_id?: string } = { form };
    if (uploadId) {
      body.upload_id = uploadId;
    }
    return this.http.put<DocumentTemplate>(`${this.environment.api_prefix}api/forms/from-upload`, body).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  deleteForm(formId: string): Observable<any> {
    return this.http.delete(`${this.environment.api_prefix}api/forms/${formId}`).pipe(
      tap(() => this.invalidateFormsCache())
    );
  }

  searchPublicForms(query: string): Observable<DocumentTemplate[]> {
    return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms/public/search`, {
      params: {
        q: encodeURIComponent(query)
      }
    });
  }

  getFormSuggestions(countyId: string, state: string, attachmentType: string): Observable<DocumentTemplate[]> {
    const params: any = {
      state: encodeURIComponent(state),
      type: encodeURIComponent(attachmentType)
    };

    if (countyId) {
      params.countyId = encodeURIComponent(countyId);
    }

    return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms/recommendations`, {
      params
    });
  }

  getFormsByType(type: AttachmentType): Observable<DocumentTemplate[]> {
    return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms/type/${type}`);
  }

  getAllForms(includeDisabled = false): Observable<DocumentTemplate[]> {
    if (includeDisabled) {
      // Bypass cache for admin views that need disabled templates
      return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms`, {
        params: { 'include-disabled': 'true' }
      });
    }
    if (!this.cachedForms$) {
      const forms$: Observable<DocumentTemplate[]> = defer(() => {
        let answered = false;
        // An HTTP interceptor can swallow a request (a 401 logout, the 412 policy gate) and complete it with
        // neither a value nor an error. shareReplay would keep that finished, empty stream for the life of
        // the page, so every later caller waited on it forever. Drop it so the next caller asks again. In tap,
        // not finalize: finalize runs after subscribers see the completion, so one that asks again from its
        // complete callback would still get the finished stream. An error needs nothing here: shareReplay
        // resets itself on error before passing it on.
        return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms`, {
          params: { 'include-disabled': 'false' }
        }).pipe(
          tap({
            next: () => { answered = true; },
            complete: () => {
              if (!answered && this.cachedForms$ === forms$) {
                this.cachedForms$ = null;
              }
            }
          })
        );
      }).pipe(shareReplay(1));
      this.cachedForms$ = forms$;
    }
    return this.cachedForms$;
  }

  /**
   * Resolves templates that a stored configuration already REFERENCES but that `getAllForms()` does not
   * return — a disabled template, or a public/built-in one (a workflow cloned from a built-in workflow
   * references public template ids). Used to name those templates for display; the add-picker still
   * offers only what `getAllForms()` returns.
   *
   * Ids that cannot be resolved (deleted, or owned by another org) are simply absent from the result —
   * the caller renders those as unavailable.
   *
   * Resolutions are cached per id and concurrent requests are deduped, so the several control instances
   * that can mount at once never refetch the same id. Callers are expected to ask only for ids they
   * could not already resolve locally, so a healthy configuration triggers no request at all.
   */
  resolveForms(ids: string[]): Observable<DocumentTemplate[]> {
    const wanted = Array.from(new Set((ids ?? []).filter(id => !!id)));

    // Requests already covering some of these ids, started by another caller. We must wait on these too,
    // not just on our own request: otherwise a caller asking for [b, c] while [a, b] is in flight would
    // fetch only c and resolve before b arrived, returning an incomplete list.
    const pending = Array.from(new Set(
      wanted.map(id => this.inFlightFormIds.get(id)).filter(Boolean)
    ));

    const missing = wanted.filter(id =>
      !this.resolvedFormsById.has(id) &&
      !this.unresolvableFormIds.has(id) &&
      !this.inFlightFormIds.has(id)
    );

    if (!missing.length) {
      // Everything is cached, already known unresolvable, or in flight for another caller.
      if (!pending.length) {
        return of(this.collectResolved(wanted));
      }
      return forkJoin(pending).pipe(map(() => this.collectResolved(wanted)));
    }

    const request$ = this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms/referenced`, {
      params: { ids: missing.join(',') }
    }).pipe(
      tap(templates => {
        (templates ?? []).forEach(template => this.resolvedFormsById.set(template.id, template));
        // Record ids that resolved to nothing so we do not re-request them for this session.
        missing.forEach(id => {
          if (!this.resolvedFormsById.has(id)) {
            this.unresolvableFormIds.add(id);
          }
        });
      }),
      finalize(() => missing.forEach(id => this.inFlightFormIds.delete(id))),
      shareReplay(1)
    );

    missing.forEach(id => this.inFlightFormIds.set(id, request$));
    // Collect only once our request AND every overlapping in-flight one has settled.
    return forkJoin([request$, ...pending]).pipe(map(() => this.collectResolved(wanted)));
  }

  /** True once resolveForms has established that this id resolves to nothing available to the org. */
  isFormUnresolvable(id: string): boolean {
    return this.unresolvableFormIds.has(id);
  }

  private collectResolved(ids: string[]): DocumentTemplate[] {
    return ids.map(id => this.resolvedFormsById.get(id)).filter(Boolean);
  }

  /**
   * Everything referencing a template — attorney workflows and PM case types — for the "Used in N
   * workflows" clause in the template header.
   *
   * Not folded into the template payload: the same DTO serves `/referenced` and the clone path, and the
   * header refreshes independently of the form body.
   */
  getUsage(id: string): Observable<DocumentTemplateUsage> {
    return this.http.get<DocumentTemplateUsage>(`${this.environment.api_prefix}api/forms/${id}/usage`);
  }

  /**
   * Which of a template's `#tokens#` resolve, and what generation does with the ones that do not.
   *
   * Its own request rather than part of the template payload: it needs the organization's custom-field
   * and judgement-option lookups, and it has to be refetched after the template's file is replaced —
   * a new file means a new token list.
   */
  getFieldReport(id: string): Observable<TemplateFieldReport> {
    return this.http.get<TemplateFieldReport>(`${this.environment.api_prefix}api/forms/${id}/fields`);
  }

  /** A template's history, newest first. Includes legacy embedded entries, flagged. */
  getAuditLog(id: string): Observable<DocumentTemplateAuditEntry[]> {
    return this.http.get<DocumentTemplateAuditEntry[]>(`${this.environment.api_prefix}api/forms/${id}/audit`);
  }

  /**
   * The history for a date range as CSV. `to` is exclusive.
   *
   * Returned as a blob rather than text so the caller can hand it straight to a download without
   * re-encoding, and so a `text/csv` response is not parsed as JSON.
   */
  exportAuditLog(id: string, from: string, to: string): Observable<Blob> {
    return this.http.get(`${this.environment.api_prefix}api/forms/${id}/audit/export`, {
      params: { from, to },
      responseType: 'blob'
    });
  }

  /** Clears the cached forms list so the next getAllForms() call fetches fresh data */
  invalidateFormsCache(): void {
    this.cachedForms$ = null;
    // A template that was just created/enabled may now resolve, and one just deleted may not.
    this.resolvedFormsById.clear();
    this.unresolvableFormIds.clear();
  }

  getAllPublicForms(includeDisabled = false): Observable<DocumentTemplate[]> {
    const params = {
      'include-disabled': includeDisabled ? 'true' : 'false'
    };
    return this.http.get<DocumentTemplate[]>(`${this.environment.api_prefix}api/forms/public`, {
      params
    });
  }

  /**
   * The case-field catalog, shared between concurrent subscribers.
   *
   * The Content tab renders every page at once and each `ee-html-editor` asks for this in `ngOnInit`, so a
   * 10-page template fired 10 identical requests — reported by the reviewer on PR #868. `shareReplay` with
   * `refCount` collapses those into one while any editor is listening, and drops the cache once they all
   * unsubscribe, so reopening the tab still gets fresh data (which matters: creating a custom field
   * changes this catalog).
   */
  /**
   * The field catalog, with concurrent callers sharing one request.
   *
   * An HTML template's Content tab mounts one editor per page, and every editor asks for the catalog in
   * the same change-detection pass — a ten-page notice fired ten identical requests. They are deduped
   * here instead of in each caller, since the same burst happens in the field-reference drawer.
   *
   * The share lasts only as long as the request: `finalize` drops it on completion so the next caller
   * fetches again. A session-lifetime cache would be stale the moment someone adds a custom field, which
   * this catalog includes — the editor's mention list would then be missing a field the user just created.
   */
  getFillableFields(): Observable<FillableFieldSection[]> {
    this.fillableFields$ ??= this.http
      .get<FillableFieldSection[]>(`${this.environment.api_prefix}api/forms/fillable-fields`)
      .pipe(
        finalize(() => {
          this.fillableFields$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    return this.fillableFields$;
  }

  private fillableFields$: Observable<FillableFieldSection[]> | null = null;

  generate(formId: string, evictionDetails: Eviction): Observable<any> {
    return this.http.post(`${this.environment.api_prefix}api/forms/${formId}/generate`, evictionDetails, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  private getAcroForm(pdfDoc: PDFDocument) {
    return pdfDoc.catalog.lookup(PDFName.of('AcroForm'), PDFDict);
  }

  private getAcroFields(pdfDoc: PDFDocument) {
    const acroForm = this.getAcroForm(pdfDoc);
    if (!acroForm) {
      return [];
    }

    const fieldRefs = acroForm.lookupMaybe(PDFName.of('Fields'), PDFArray);
    if (!fieldRefs) {
      return [];
    }

    const fields = new Array(fieldRefs.size());
    for (let idx = 0, len = fieldRefs.size(); idx < len; idx++) {
      fields[idx] = fieldRefs.lookup(idx);
    }
    return fields;
  }

  private findAcroFieldByName(pdfDoc: PDFDocument, name: string) {
    const acroFields = this.getAcroFields(pdfDoc);
    return acroFields.find((acroField) => {
      const fieldName = acroField.get(PDFName.of('T'));
      return (
        (fieldName instanceof PDFString || fieldName instanceof PDFHexString) &&
        fieldName.decodeText() === name
      );
    });
  }

  private fillAcroTextField(acroField: PDFDict, text: string, font: PDFFont) {
    const rect = acroField.lookupMaybe(PDFName.of('Rect'), PDFArray);
    if (rect) {
      const width =
        rect.lookup(2, PDFNumber).asNumber() - rect.lookup(0, PDFNumber).asNumber();
      const height =
        rect.lookup(3, PDFNumber).asNumber() - rect.lookup(1, PDFNumber).asNumber();

      const MK = acroField.lookupMaybe(PDFName.of('MK'), PDFDict);
      const R = MK && MK.lookupMaybe(PDFName.of('R'), PDFNumber);
      const rotation = R ? R.asNumber() : 0;

      const N = this.singleLineAppearanceStream(font, text, rotation, width, height);
      acroField.set(PDFName.of('AP'), acroField.context.obj({N}));
    }

    // acroField.set(PDFName.of('Ff'), PDFNumber.of(1 /* Read Only */));
    acroField.set(PDFName.of('V'), PDFHexString.fromText(text));
    acroField.delete(PDFName.of('AP'));
  }

  private singleLineAppearanceStream(
    font: PDFFont,
    text: string,
    rotation: number,
    width: number,
    height: number,
  ) {
    const rotationCorrectedHeight = rotation % 90 === 0 ? width : height;

    const size = font.sizeAtHeight(rotationCorrectedHeight - 8);
    const encodedText = font.encodeText(text);
    const x = 0;
    const y = rotationCorrectedHeight - size;

    return this.textFieldAppearanceStream(
      font,
      size,
      encodedText,
      rotation,
      x,
      y,
      width,
      height,
    );
  }

  private textFieldAppearanceStream(
    font: PDFFont,
    size: number,
    encodedText: PDFHexString,
    rotation: number,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    const dict = font.doc.context.obj({
      Type: 'XObject',
      Subtype: 'Form',
      FormType: 1,
      BBox: [0, 0, width, height],
      Resources: {Font: {F0: font.ref}},
    });

    const operators = [
      rotateDegrees(rotation),
      translate(0, rotation % 90 === 0 ? -width : 0),
      PDFOperator.of(Ops.BeginMarkedContent, [asPDFName('Tx')]),
      pushGraphicsState(),
      ...drawText(encodedText, {
        color: rgb(0, 0, 0),
        font: 'F0',
        size,
        rotate: degrees(0),
        xSkew: degrees(0),
        ySkew: degrees(0),
        x,
        y,
      }),
      popGraphicsState(),
      PDFOperator.of(Ops.EndMarkedContent)
    ];

    const stream = PDFContentStream.of(dict, operators);

    return font.doc.context.register(stream);
  }
}
